import { InstagramInfluencerUser, PostPaymentRequest, postPostPaymentRequest } from '@round/api';
import { Modal, ModalTitle, Button } from '@round/ui-kit';
import { OptionsContext } from 'contexts/OptionsContext/OptionsContext';
import { FormikHelpers, useFormik, FormikProvider } from 'formik';
import useNonNullContext from 'Hooks/useNonNullContext';
import { InstagramInfluencerPost } from 'Modules/Advertising/InfluencerPlan/types/Instagram.types';
import { useMemo } from 'react';
import styles from './PostPaymentRequestModal.module.css';
import { asMoney, showNotification } from 'helpers';
import { mapApiErrorsToFormikErrors } from 'utility/utility';
import PostPaymentRequestForm, { PaymentRequestFormValues } from '../components/PostPaymentRequestForm';
import { baseInitialValues, getPaymentMethodAPIValues, validationSchema } from '../helpers';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    instagramInfluencerPost: InstagramInfluencerPost | undefined | null;
    influencerUser: InstagramInfluencerUser | undefined | null;
    onSuccess: (paymentRequest: PostPaymentRequest) => void;
};

const hasNumericalMismatch = (a: number | undefined | null, b: number | undefined | null) => {
    if (typeof a !== 'number' || typeof b !== 'number') {
        return false;
    }

    return a !== b;
};

const PostPaymentRequestModal = ({ isOpen, onClose, influencerUser, instagramInfluencerPost, onSuccess }: Props) => {
    const { currencies } = useNonNullContext(OptionsContext);
    const currencyOptions = currencies.map((c) => ({
        value: c.id,
        label: c.name,
    }));

    const initialValues: PaymentRequestFormValues = useMemo(
        () => ({
            ...baseInitialValues,
            amount: instagramInfluencerPost?.cost || '',
            currency: currencyOptions.find((curr) => curr.value === instagramInfluencerPost?.currency_id),
            paypal_email_address: influencerUser?.paypal_email || '',
        }),
        [
            currencyOptions,
            influencerUser?.paypal_email,
            instagramInfluencerPost?.cost,
            instagramInfluencerPost?.currency_id,
        ]
    );

    const handleSubmit = async (values: PaymentRequestFormValues, helpers: FormikHelpers<PaymentRequestFormValues>) => {
        if (typeof instagramInfluencerPost?.id !== 'number' || !values.currency?.value) {
            return;
        }

        helpers.setSubmitting(true);
        try {
            const response = await postPostPaymentRequest({
                amount: values.amount,
                currency: values.currency?.value,
                instagram_influencer_post: instagramInfluencerPost?.id,
                ...getPaymentMethodAPIValues(values),
            });

            if (response.status === 201) {
                showNotification('Payment request created', 'info');
                onClose();
                onSuccess(response.data);
                return;
            }

            if (response.status === 400) {
                if (Array.isArray(response.data)) {
                    showNotification(response.data.join('. '), 'error');
                    return;
                }

                helpers.setErrors(mapApiErrorsToFormikErrors<PaymentRequestFormValues>(response.data));
                return;
            }

            showNotification(response.data.detail, 'error');
        } catch {
            showNotification('Could not request payment', 'error');
        } finally {
            helpers.setSubmitting(false);
        }
    };

    const reelsCurrency = currencies.find((curr) => curr.id === influencerUser?.reels_currency);
    const reelsCostDisplayValue =
        influencerUser?.reels_cost && reelsCurrency ? asMoney(Number(influencerUser.reels_cost), reelsCurrency) : '-';

    const feedCurrency = currencies.find((curr) => curr.id === influencerUser?.feed_currency);
    const feedCostDisplayValue =
        influencerUser?.feed_cost && feedCurrency ? asMoney(Number(influencerUser.feed_cost), feedCurrency) : '-';

    const storyCurrency = currencies.find((curr) => curr.id === influencerUser?.story_currency);
    const storyCostDisplayValue =
        influencerUser?.story_cost && storyCurrency ? asMoney(Number(influencerUser.story_cost), storyCurrency) : '-';

    const postCurrency = currencies.find((curr) => curr.id === instagramInfluencerPost?.currency_id);
    const postCostDisplayValue =
        instagramInfluencerPost?.cost && postCurrency
            ? asMoney(parseFloat(instagramInfluencerPost.cost), postCurrency)
            : '-';

    const formikProps = useFormik<PaymentRequestFormValues>({
        initialValues: initialValues,
        validationSchema: validationSchema,
        onSubmit: handleSubmit,
        enableReinitialize: true,
    });

    const currencyFieldValue = formikProps.values.currency?.value;
    const hasCurrencyMismatch = hasNumericalMismatch(currencyFieldValue, influencerUser?.reels_currency);

    const handleClose = () => {
        formikProps.resetForm();
        onClose();
    };

    return (
        <Modal closeOnOverlayClick isOpen={isOpen} onClose={handleClose} className={styles.container}>
            <ModalTitle>Request Payment</ModalTitle>
            <dl className={styles.detailsContainer}>
                <div>
                    <dt>Reels rate</dt>
                    <dd>{reelsCostDisplayValue}</dd>
                </div>

                <div>
                    <dt>Feed rate</dt>
                    <dd>{feedCostDisplayValue}</dd>
                </div>

                <div>
                    <dt>Story rate</dt>
                    <dd>{storyCostDisplayValue}</dd>
                </div>

                <div>
                    <dt>Post cost</dt>
                    <dd>{postCostDisplayValue}</dd>
                </div>
            </dl>

            <FormikProvider value={formikProps}>
                <>
                    <PostPaymentRequestForm />

                    <Button
                        type="filled"
                        color="black"
                        loading={formikProps.isSubmitting}
                        disabled={!formikProps.isValid}
                        className={styles.submitButton}
                        onClick={formikProps.submitForm}
                    >
                        Submit
                    </Button>

                    {hasCurrencyMismatch && (
                        <p className={styles.warning}>
                            The selected currency does not match the influencer's requested currency.
                        </p>
                    )}
                </>
            </FormikProvider>
        </Modal>
    );
};

export default PostPaymentRequestModal;
